import { createUrlWithCenterId, getAuthHeaders } from '../utils/apiHelpers';
import { API_URL } from '../config';

const VIDEO_EXT = /\.(mp4|webm|mov|m4v)$/i;
const PROXY_MAX_BYTES = 10 * 1024 * 1024;

export interface ArchivoDto {
  id: number;
  nombre?: string;
  fecha: string;
  storageKey?: string;
  contentType?: string;
  sizeBytes?: number;
}

export interface ArchivoView {
  url: string;
  contentType: string;
  fileName?: string;
  /** Liberar object URL si la vista usó blob proxy. */
  revoke?: () => void;
}

/** Subida genérica hacia MinIO/S3 vía API (fotos/PDFs ≤ 10 MB). */
export const uploadArchivo = async (file: File, category: string): Promise<ArchivoDto> => {
  const formData = new FormData();
  formData.append('file', file);
  const path = createUrlWithCenterId('/api/Archivos/upload');
  const sep = path.includes('?') ? '&' : '?';
  const url = `${API_URL}${path}${sep}category=${encodeURIComponent(category)}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`Error al subir archivo: ${response.status}`);
  }
  return response.json();
};

/** Videos o archivos grandes: presign → PUT directo a MinIO/S3 → complete. */
export const presignUploadArchivo = async (file: File, category: string): Promise<ArchivoDto> => {
  const path = createUrlWithCenterId('/api/Archivos/presign-upload');
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || undefined,
      category,
      sizeBytes: file.size,
    }),
  });
  if (!res.ok) {
    throw new Error(`Error al preparar subida: ${res.status}`);
  }
  const data = await res.json();
  const put = await fetch(data.putUrl as string, {
    method: 'PUT',
    headers: {
      'Content-Type': (data.contentType as string) || file.type || 'application/octet-stream',
    },
    body: file,
  });
  if (!put.ok) {
    throw new Error(`Error al subir a storage: ${put.status}`);
  }
  const completePath = createUrlWithCenterId(`/api/Archivos/${data.id}/complete`);
  const complete = await fetch(`${API_URL}${completePath}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!complete.ok) {
    throw new Error(`Error al confirmar subida: ${complete.status}`);
  }
  return complete.json();
};

/** Elige multipart (fotos/docs chicos) o PUT prefirmado (videos). */
export const uploadMedia = async (file: File, category: string): Promise<ArchivoDto> => {
  if (VIDEO_EXT.test(file.name) || file.size > PROXY_MAX_BYTES) {
    return presignUploadArchivo(file, category);
  }
  return uploadArchivo(file, category);
};

export const getArchivoView = async (archivoId: number): Promise<ArchivoView> => {
  const path = createUrlWithCenterId(`/api/Archivos/${archivoId}/download`);
  const response = await fetch(`${API_URL}${path}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Error al descargar archivo: ${response.status}`);
  }

  const ct = response.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    const data = await response.json();
    if (data.mode === 'presigned' && data.url) {
      return {
        url: data.url as string,
        contentType: (data.contentType as string) || 'application/octet-stream',
        fileName: data.fileName as string | undefined,
      };
    }
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  return {
    url: objectUrl,
    contentType: blob.type || ct || 'application/octet-stream',
    revoke: () => window.URL.revokeObjectURL(objectUrl),
  };
};

export const isVideoContentType = (contentType?: string): boolean =>
  (contentType || '').toLowerCase().startsWith('video/');

/** Descarga: respuesta binaria o JSON con URL prefirmada. */
export const downloadArchivoById = async (archivoId: number): Promise<void> => {
  const path = createUrlWithCenterId(`/api/Archivos/${archivoId}/download`);
  const url = `${API_URL}${path}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Error al descargar archivo: ${response.status}`);
  }

  const ct = response.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    const data = await response.json();
    if (data.mode === 'presigned' && data.url) {
      window.open(data.url as string, '_blank', 'noopener,noreferrer');
      return;
    }
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'archivo';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(objectUrl);
  document.body.removeChild(a);
};
